import { useUIStore, UIMode } from '../store/useUIStore';

interface ResponsiveMode {
  // Current UI mode
  currentMode: UIMode;
  
  // Boolean helpers
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouch: boolean;
  
  // Orientation
  orientation: 'portrait' | 'landscape';
  isPortrait: boolean;
  isLandscape: boolean;
  
  // Combined helpers
  isMobileOrTablet: boolean;
  isTabletOrDesktop: boolean;
  
  // Device capabilities
  hasPen: boolean;
  isIPad: boolean;
  isLargeTablet: boolean; // 10+ inch tablets
  
  // Actions
  setMode: (mode: UIMode) => void;
  setAutoMode: (auto: boolean) => void;
}

/**
 * Hook to get current responsive mode and related helpers
 * Replaces the old useIsMobile hook with more detailed information
 */
export const useResponsiveMode = (): ResponsiveMode => {
  const { currentMode, orientation, setMode, setAutoMode, hasPen, isIPad, screenSize } = useUIStore();
  
  return {
    // Current mode
    currentMode,
    
    // Boolean helpers
    isMobile: currentMode === 'mobile',
    isTablet: currentMode === 'tablet',
    isDesktop: currentMode === 'desktop',
    isTouch: currentMode === 'mobile' || currentMode === 'tablet',
    
    // Orientation
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape',
    
    // Combined helpers
    isMobileOrTablet: currentMode === 'mobile' || currentMode === 'tablet',
    isTabletOrDesktop: currentMode === 'tablet' || currentMode === 'desktop',
    
    // Device capabilities
    hasPen,
    isIPad,
    isLargeTablet: currentMode === 'tablet' && (screenSize === 'large' || screenSize === 'xlarge'),
    
    // Actions
    setMode,
    setAutoMode
  };
};

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useResponsiveMode instead
 */
export const useIsMobile = (): boolean => {
  const { isMobile } = useResponsiveMode();
  return isMobile;
};